// @givmo/passkeys — what a sign-in door shows where it used to show its red error line (AS-WEB27):
// the company lock's refusal as `LockRefusalNotice` (never the server's raw words), and every other
// failure exactly as before. One choice, made once, for every door of every console: the charity
// console's chooser, email form and Board's director door, the employer portal's chooser and email
// form, the company console's sign-in page, and the second-factor prompts that finish one.
//
// The door supplies what only it knows: where the way back in opens (its console's
// `/lost-company-access`, returning to this door), its organization door where it offers one
// (AS-WEB30), and a host's one line (Board's, from an invitation). While this shows the notice, the
// door leaves its own entry link out: the notice carries the way back in itself.

import type { ReactNode } from 'react';
import { ErrorMessage } from '@givmo/design-system';
import { LockRefusalNotice, type LockRefusalNoticeProps } from './lock-refusal-notice';

export interface SignInDoorErrorProps {
  /** The door's error line, or null when there is nothing to show. */
  error: string | null;
  /** Whether that error was the company's sign-in lock refusing the sign-in (`organizationSsoRequired`). */
  lockRefused: boolean;
  /** Opens the way back in. Absent, a lock refusal shows as its plain line (a surface with no flow). */
  onUnlink?: (() => void) | undefined;
  /** The organization door, where this page offers it (AS-WEB30). */
  organizationSso?: LockRefusalNoticeProps['organizationSso'];
  /** A host's one line in the notice (Board's, from an invitation). */
  line?: ReactNode;
  /** The red line's testid, as each door already names it. */
  testId?: string;
}

export function SignInDoorError({
  error,
  lockRefused,
  onUnlink,
  organizationSso,
  line,
  testId = 'auth-error',
}: SignInDoorErrorProps) {
  if (error === null) return null;
  if (lockRefused && onUnlink !== undefined) {
    return <LockRefusalNotice onUnlink={onUnlink} organizationSso={organizationSso} line={line} />;
  }
  return <ErrorMessage testId={testId}>{error}</ErrorMessage>;
}
